// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  OAuthProvider,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState('free'); // "free" | "pro" | "trial"
  const [loading, setLoading] = useState(true);

  // 🔐 Helper: ensure user doc exists + set default subscription if needed
  const ensureUserDoc = async (user) => {
    if (!user) return;
    try {
      const userRef = doc(db, 'users', user.uid);
      const snap = await getDoc(userRef);
      if (!snap.exists()) {
        // 👇 Adjust these fields if your iOS app uses a different schema
        await setDoc(
          userRef,
          {
            email: user.email || null,
            createdAt: serverTimestamp(),
            subscriptionStatus: 'free',
          },
          { merge: true }
        );
        setSubscriptionStatus('free');
      } else {
        const data = snap.data();
        setSubscriptionStatus(data.subscriptionStatus || 'free');
      }
    } catch (err) {
      console.error('Error ensuring user doc / subscriptionStatus:', err);
      setSubscriptionStatus('free');
    }
  };

  // 👀 Watch auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await ensureUserDoc(user);
      } else {
        setSubscriptionStatus('free');
      }
      setLoading(false);
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 📩 Email/password login
  const login = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  // 🆕 Email/password signup
  const signup = async (email, password) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await ensureUserDoc(cred.user);
    return cred;
  };

  // 🔓 Logout
  const logout = () => signOut(auth);

  // 🟦 Google sign-in
  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    await ensureUserDoc(result.user);
    return result;
  };

  // 🍎 Apple sign-in (if configured in Firebase)
  const signInWithApple = async () => {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    const result = await signInWithPopup(auth, provider);
    await ensureUserDoc(result.user);
    return result;
  };

  const value = {
    currentUser,
    loading,
    subscriptionStatus,
    login,
    signup,
    logout,
    signInWithGoogle,
    signInWithApple,
  };

  return (
    <AuthContext.Provider value={value}>
      {/* Prevent app render until we know auth + subscription state */}
      {!loading && children}
    </AuthContext.Provider>
  );
}
